/**
 * The window which changes one rule of the guard.
 *
 * One window for the whole listing: the button pressed says which rule it is
 * about, and its values are written into the fields before the window opens.
 * Drawing one window per row would repeat five fields once for every rule on
 * the page.
 *
 * The button is a link, and the link is where a browser with no windows of
 * its own lands: the same screen with the form below already filled in with
 * that rule. Only the click is taken over, and only when there is a window to
 * take it over with.
 */

import { dialogsWork } from "./modal";
import { query, queryAll } from "../core/dom";

/** What a button carries about the rule it edits, by field name. */
function valuesOf(opener: HTMLElement): Record<string, string> {
    return {
        id: opener.dataset["ruleId"] ?? "",
        kind: opener.dataset["ruleKind"] ?? "",
        action: opener.dataset["ruleAction"] ?? "",
        pattern: opener.dataset["rulePattern"] ?? "",
        // What the rule has left, so changing its wording does not also
        // turn an hour-long punishment into one which never runs out.
        hours: opener.dataset["ruleHours"] ?? "0",
        note: opener.dataset["ruleNote"] ?? "",
    };
}

/** Point the window at one rule and open it. */
export function openRuleDialog(dialog: HTMLDialogElement, form: HTMLFormElement, opener: HTMLElement): void {
    const values = valuesOf(opener);

    for (const field of queryAll<HTMLInputElement | HTMLSelectElement>("[data-rule-field]", form)) {
        field.value = values[field.dataset["ruleField"] ?? ""] ?? "";
    }

    dialog.showModal();
    query<HTMLInputElement>("[data-rule-field='pattern']", form)?.focus();
}

/** Wire every edit button of the current page. */
export function initRuleDialog(): void {
    const dialog = query<HTMLDialogElement>("[data-rule-dialog]");
    const openers = queryAll<HTMLElement>("[data-rule-edit]");

    if (!dialog || openers.length === 0 || !dialogsWork()) {
        return;
    }

    const form = query<HTMLFormElement>("[data-rule-form]", dialog);
    if (!form) {
        return;
    }

    for (const opener of openers) {
        opener.addEventListener("click", (event: MouseEvent) => {
            // A middle click, or one with a key held, is somebody asking for
            // another tab. That is the link's business, not the window's.
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
                return;
            }

            event.preventDefault();
            openRuleDialog(dialog, form, opener);
        });
    }

    query<HTMLElement>("[data-rule-cancel]", dialog)?.addEventListener("click", () => dialog.close());
}
